import { ItemWithMetaData, ProfileAddedBy } from "../api/Items";

export type TrustBucket =
  | "strong"
  | "potential"
  | "needs_verification"
  | "review";

export type ProfileProvenance = { label: string; className: string };

export function getProfileProvenance(
  addedBy: string | null | undefined
): ProfileProvenance {
  if (addedBy === ProfileAddedBy.CLIENT) {
    return { label: "Provided", className: "bg-blue-100 text-blue-700" };
  }
  return { label: "Discovered", className: "bg-green-100 text-green-700" };
}

export function formatProfileAddedByLabel(
  addedBy: string | null | undefined
): string | null {
  if (!addedBy) {
    return null;
  }
  const normalized = addedBy.toUpperCase();
  if (normalized === ProfileAddedBy.CLIENT) {
    return "Client";
  }
  if (
    normalized === ProfileAddedBy.INVESTIGATOR ||
    normalized === ProfileAddedBy.VETRIC
  ) {
    return "Investigator";
  }
  return null;
}

// Mirrors actor_service.vetric.scoring.SOURCE_POINTS so the UI can pick the
// strongest "how it was found" reason for a profile.
const SOURCE_POINTS: Record<string, number> = {
  email: 3,
  existing: 3,
  handle: 2,
  cross_platform_id: 2,
  cross_platform_link: 2,
  link_match: 2,
  name: 1,
  handle_fuzzy: 1,
  phone: 1,
  birthdate: 1,
  age: 1,
  location: 1,
  education: 1,
  job: 1,
  search: 0,
};

export const TRUST_BUCKET_LABELS: Record<TrustBucket, string> = {
  strong: "Strong Match",
  potential: "Potential Match",
  needs_verification: "Need Review",
  // Web-content-scraper fallback when no Vetric trust/discovery chip applies.
  review: "Review",
};

export const TRUST_BUCKET_ORDER: TrustBucket[] = [
  "strong",
  "potential",
  "needs_verification",
  "review",
];

export interface ProfileTrust {
  hasTrust: boolean;
  bucket: TrustBucket;
  score: number;
  sources: string[];
}

function bucketFromScore(score: number): TrustBucket {
  // Mirrors actor_service.vetric.scoring: >=5 strong, 4 potential, 3 needs
  // review. Scores <= 2 are removed on the backend and not persisted; if a
  // legacy item still has one, show it as Need Review rather than Potential.
  if (score >= 5) return "strong";
  if (score >= 4) return "potential";
  return "needs_verification";
}

function isTrustBucket(value: unknown): value is TrustBucket {
  return (
    value === "strong" ||
    value === "potential" ||
    value === "needs_verification" ||
    value === "review"
  );
}

export function getProfileTrust(item: ItemWithMetaData): ProfileTrust {
  const content = item.item.content || {};
  const explicitSources: string[] = Array.isArray(content.discovery_sources)
    ? content.discovery_sources
    : [];
  // Fall back to the sources implied by discovery_matches so a profile that only
  // carries match data (no discovery_sources/trust_score/trust_bucket) still
  // gets trust ranking, bucket pills, and match badges instead of being treated
  // as a legacy profile.
  const matches: DiscoveryMatch[] = Array.isArray(content.discovery_matches)
    ? content.discovery_matches
    : [];
  const sources: string[] =
    explicitSources.length > 0
      ? explicitSources
      : matches.map((m) => m.source).filter(Boolean);
  const hasScore =
    typeof content.trust_score === "number" ||
    typeof content.trust_bucket === "string" ||
    sources.length > 0;
  const score =
    typeof content.trust_score === "number"
      ? content.trust_score
      : sources.reduce((acc, s) => acc + (SOURCE_POINTS[s] ?? 0), 0);
  // Only trust a server-provided bucket when it is a known value; otherwise an
  // unexpected string would flow into BUCKET_PILL_STYLES / TRUST_BUCKET_LABELS
  // and render a pill with no label or styling.
  const bucket: TrustBucket = isTrustBucket(content.trust_bucket)
    ? content.trust_bucket
    : bucketFromScore(score);
  if (hasScore) {
    return { hasTrust: true, bucket, score, sources };
  }
  // Web-content hits have no Vetric trust fields; surface a Review pill so they
  // still get a chip in the profiler Review list (styled like Potential Match).
  if (content.added_by === ProfileAddedBy.WEB_CONTENT_SCRAPER) {
    return { hasTrust: true, bucket: "review", score: 0, sources: [] };
  }
  return { hasTrust: false, bucket, score, sources };
}

export interface TrustBadge {
  label: string;
  className: string;
}

export const SOURCE_BADGES: Record<string, TrustBadge> = {
  email: {
    label: "Exposed email match",
    className: "bg-green-100 text-green-800",
  },
  existing: {
    label: "Verified existing profile",
    className: "bg-green-100 text-green-800",
  },
  handle: {
    label: "Direct handle match",
    className: "bg-green-100 text-green-800",
  },
  handle_fuzzy: {
    label: "Similar handle match",
    className: "bg-lime-100 text-lime-800",
  },
  name: {
    label: "Name match",
    className: "bg-lime-100 text-lime-800",
  },
  phone: {
    label: "Phone match",
    className: "bg-sky-100 text-sky-800",
  },
  birthdate: {
    label: "Birthdate match",
    className: "bg-sky-100 text-sky-800",
  },
  age: {
    label: "Age match",
    className: "bg-sky-100 text-sky-800",
  },
  location: {
    label: "Location match",
    className: "bg-sky-100 text-sky-800",
  },
  education: {
    label: "Education match",
    className: "bg-sky-100 text-sky-800",
  },
  job: {
    label: "Job match",
    className: "bg-sky-100 text-sky-800",
  },
  cross_platform_id: {
    label: "Linked from another profile",
    className: "bg-teal-100 text-teal-800",
  },
  cross_platform_link: {
    label: "From their bio link",
    className: "bg-amber-100 text-amber-800",
  },
  link_match: {
    label: "Matched from their link",
    className: "bg-amber-100 text-amber-800",
  },
  search: {
    label: "Name search \u2014 verify",
    className: "bg-gray-100 text-gray-700",
  },
};

// Short labels for profile-card InfoChips (Figma: "Exact Match", "Direct Handle", etc.).
const SOURCE_CHIP_LABELS: Record<string, string> = {
  handle: "Direct Handle",
  existing: "Existing Profile",
  cross_platform_id: "Linked Profile",
  cross_platform_link: "Bio Link",
  link_match: "Link Match",
  search: "Name Search",
  name: "Name Search",
};

export interface ProfileInfoChip {
  label: string;
  value: string;
}

// The strongest reason the profile was discovered, used as the primary badge.
export function primaryTrustBadge(sources: string[]): TrustBadge | undefined {
  if (!sources || sources.length === 0) return undefined;
  const best = [...sources].sort(
    (a, b) => (SOURCE_POINTS[b] ?? 0) - (SOURCE_POINTS[a] ?? 0)
  )[0];
  return (
    SOURCE_BADGES[best] || {
      label: best,
      className: "bg-gray-100 text-gray-700",
    }
  );
}

export function allTrustBadges(sources: string[]): TrustBadge[] {
  return (sources || []).map(
    (s) =>
      SOURCE_BADGES[s] || { label: s, className: "bg-gray-100 text-gray-700" }
  );
}

export interface DiscoveryMatch {
  source: string;
  value?: string;
}

// "How we found it" badges: friendly source label + the matched value, e.g.
// "Direct handle match: User123". Sorted strongest-first by SOURCE_POINTS.
export function discoveryMatchBadges(
  matches: DiscoveryMatch[] | undefined
): TrustBadge[] {
  if (!Array.isArray(matches) || matches.length === 0) return [];
  return [...matches]
    .sort(
      (a, b) => (SOURCE_POINTS[b.source] ?? 0) - (SOURCE_POINTS[a.source] ?? 0)
    )
    .map((match) => {
      const base = SOURCE_BADGES[match.source] || {
        label: match.source,
        className: "bg-gray-100 text-gray-700",
      };
      const label = match.value ? `${base.label}: ${match.value}` : base.label;
      return { label, className: base.className };
    });
}

// Discovery matches as profile-card InfoChips, sorted strongest-first.
export function discoveryMatchChips(
  matches: DiscoveryMatch[] | undefined
): ProfileInfoChip[] {
  if (!Array.isArray(matches) || matches.length === 0) return [];
  return [...matches]
    .sort(
      (a, b) => (SOURCE_POINTS[b.source] ?? 0) - (SOURCE_POINTS[a.source] ?? 0)
    )
    .flatMap((match) => {
      if (!match.value) return [];
      const label =
        SOURCE_CHIP_LABELS[match.source] ||
        SOURCE_BADGES[match.source]?.label ||
        match.source;
      return [{ label, value: match.value }];
    });
}

// Sort by match strength: strong -> potential -> needs verification. Equal
// scores fall back to a stable URL order so the list never reshuffles between
// renders.
export function sortProfilesByTrustScore(
  profiles: ItemWithMetaData[]
): ItemWithMetaData[] {
  return [...profiles].sort((a, b) => {
    const scoreDiff = getProfileTrust(b).score - getProfileTrust(a).score;
    if (scoreDiff !== 0) return scoreDiff;
    return (a.item.content.url || "").localeCompare(b.item.content.url || "");
  });
}

export function sortProfilesByPlatform(
  profiles: ItemWithMetaData[]
): ItemWithMetaData[] {
  const platformOrder = [
    "facebook.com",
    "instagram.com",
    "linkedin.com",
    "reddit.com",
    "tiktok.com",
    "twitter.com",
    "x.com",
  ];

  return profiles.sort((a, b) => {
    const urlA = a.item.content.url.toLowerCase();
    const urlB = b.item.content.url.toLowerCase();

    const indexA = platformOrder.findIndex((platform) =>
      urlA.includes(platform)
    );
    const indexB = platformOrder.findIndex((platform) =>
      urlB.includes(platform)
    );

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    } else if (indexA !== -1) {
      return -1;
    } else if (indexB !== -1) {
      return 1;
    } else {
      return urlA.localeCompare(urlB);
    }
  });
}

/** Newest session adds first; untracked profiles keep their relative order. */
export function sortProfilesByRecentAddOrder(
  profiles: ItemWithMetaData[],
  recentlyAddedOrder: readonly string[]
): ItemWithMetaData[] {
  if (recentlyAddedOrder.length === 0) {
    return profiles;
  }

  const orderIndex = new Map(
    recentlyAddedOrder.map((id, index) => [id, index])
  );

  return [...profiles].sort((a, b) => {
    const aIndex = orderIndex.get(a.item.external_id);
    const bIndex = orderIndex.get(b.item.external_id);
    if (aIndex !== undefined && bIndex !== undefined) {
      return aIndex - bIndex;
    }
    if (aIndex !== undefined) {
      return -1;
    }
    if (bIndex !== undefined) {
      return 1;
    }
    return 0;
  });
}
